import { DatePipe } from "@angular/common";
import { EvDialogModule } from "ev-dialog";
import { EvInputReactiveModule } from "ev-input-reactive";
import { EvDropdownReactiveModule } from "ev-dropdown-reactive";
import { Component, OnInit, computed, effect, inject, input, output, signal } from "@angular/core";
import { EVToggleComponent, EVToggleStyle } from "src/app/shared-components/ev-toggle/toggle.component";
import { DefaultConverterPipe } from "src/app/shared/pipes/defaultconverter.pipe";
import { IDropDownItem } from "ev-dropdown-reactive";
import { InfoPageAccordionComponent } from "../info-page-accordion/info-page-accordion.component";
import { AccordionIdentity, AccordionStateClass } from "../../models/accordion-state.class";
import { AppraisalDenialReasonDetails, AppraisalDeniedReason, AppraisalDialogApprovalStatus, CommercialReportApprovalModel, ReportApprovalModel, ReportApprovalStatus } from "../../../../models/estate/appraisal.model";
import { EvAuthorizationService } from "src/util/auth0/ev-auth.service";
import { AppraisalService } from "src/services/appraisal-data.service";
import { AppraisalReportApprovalStatus, ValueHistoryType } from "src/models/estate/valuation-history.model";

@Component({
    selector: "appraisal-verification",
    templateUrl: "./appraisal-verification.component.html",
    styleUrls: ["./appraisal-verification.component.scss"],
    imports: [
        EvDialogModule,
        EVToggleComponent,
        EvInputReactiveModule,
        InfoPageAccordionComponent,
        DatePipe,
        EvDropdownReactiveModule,
        DefaultConverterPipe
    ]
})
export class AppraisalVerificationComponent implements OnInit {
  visible = input(false);
  appraisalValues = input.required<AppraisalValues>();
  appraisalDeniedReasons = input.required<AppraisalDenialReasonDetails[]>();
  close = output<void>();
  confirm = output<void>();

  readonly authorizationService = inject(EvAuthorizationService);
  private readonly appraisalService = inject(AppraisalService);
  defaultSignature: string = '';

  appraisalHistory = signal<ReportApprovalStatus[]>([]);

  appraisalDialogApprovalStatus = AppraisalDialogApprovalStatus
  reportApprovalStatus = AppraisalReportApprovalStatus;
  verificationApprovalEnum: AppraisalDialogApprovalStatus = AppraisalDialogApprovalStatus.Ubehandlet;

  ToggleStyle = EVToggleStyle;
  accordionIdentity = AccordionIdentity;
  appraisalHistoryAccordionState = new AccordionStateClass<ReportApprovalStatus[]>(
    null,
    "Historikk",
    AccordionIdentity.AppraisalHistory,
    "info text",
    "Fant ingen historikk",
    null,
    null,
    () => this.appraisalHistory().length
  );
  verificationComment: string;
  signature: string;
  validInput: boolean;
  valueHistoryType= ValueHistoryType;
  appraisalDeniedReason: AppraisalDeniedReason;

  readonly title = computed(() => {
    const { valueHistoryType, refId } = this.appraisalValues() ?? {};
    if (!valueHistoryType) {
      return '';
    }
    switch (valueHistoryType) {
      case ValueHistoryType.CommercialAppraisalBank:
        return 'Behandling av næringstakst #' + refId;
      case ValueHistoryType.PreAppraisalBank:
        return 'Behandling av evForhåndstakst #' + refId;
      default:
        return 'Behandling av etakst #' + refId;
    }
  });

  readonly filteredDeniedReasons = computed<AppraisalDenialReasonDetails[]>(() => {
    switch (this.appraisalValues()?.valueHistoryType) {
      case undefined:
      case null:
      case ValueHistoryType.CommercialAppraisalBank:
        return [];
      case ValueHistoryType.PreAppraisalBank:
        return this.appraisalDeniedReasons().filter(reason => reason.isUsedInPreappraisal === true);
      default:
        return this.appraisalDeniedReasons();
    }
  });

  readonly dropDownItems = computed<IDropDownItem[]>(() => {
    if (!this.appraisalValues()?.valueHistoryType) {
      return [];
    }
    return [
      ...this.filteredDeniedReasons().map(reason => ({ key: reason.appraisalDeniedReason, value: reason.appraisalDeniedReasonDescription })),
      { key: null, value: "Annet..." }
    ];
  });

  constructor() {
    effect(() => {
      const currentValue = this.appraisalValues();

      if (currentValue?.reportId) {
        if (currentValue.valueHistoryType === ValueHistoryType.Takst) {
          this.getExternalAppraisalReportApprovalHistory(currentValue.reportId);
        } else {
          this.getAppraisalReportApprovalHistory(currentValue.reportId);
        }
      }

      this.resetState();
    });
  }

  ngOnInit() {
    const email = this.authorizationService?.getEmailAddress();
    if (email && !email.includes('local')) {
      this.defaultSignature = email;
    }
    this.resetState();
  }

  onKey(value: string) {
    this.verificationComment = value;
    this.checkDisableValue();
  }

  chooseDeniedReason(item: IDropDownItem) {
    if (this.appraisalDeniedReason === null && item.key !== null) {
      this.verificationComment = null;
    }
    this.appraisalDeniedReason = this.filteredDeniedReasons().find(x => x.appraisalDeniedReason === item.key)?.appraisalDeniedReason ?? null;
    this.checkDisableValue();
  }

  onSigningKey(e: Event) {
    const target = e.target as HTMLInputElement;
    const value = target.value;
    this.signature = value;
    this.checkDisableValue();
  }

  confirmVerification() {
    const model: ReportApprovalModel = new ReportApprovalModel(
      this.appraisalValues().reportId,
      this.verificationApprovalEnum,
      this.appraisalDeniedReason,
      this.verificationComment,
      this.signature
    );
    if (this.appraisalValues().valueHistoryType === ValueHistoryType.Takst) {
      this.postExternalAppraisalApproval(model);
    } else if (this.appraisalValues().valueHistoryType === ValueHistoryType.CommercialAppraisalBank) {
      const commercialModel: CommercialReportApprovalModel = new CommercialReportApprovalModel(
        this.appraisalValues().refId,
        this.appraisalValues().reportId,
        this.verificationApprovalEnum,
        this.verificationComment,
        this.signature
      );
    }
    else {
      this.postAppraisalApproval(model);
    }
  }

  closeDialog() {
    this.close.emit();
  }

  changeDenyAppraisal() {
    if (this.verificationApprovalEnum === AppraisalDialogApprovalStatus.Avvist) {
      this.verificationApprovalEnum = AppraisalDialogApprovalStatus.Ubehandlet;
      this.verificationComment = "";
      this.appraisalDeniedReason = null;
    } else {
      this.verificationApprovalEnum = AppraisalDialogApprovalStatus.Avvist;
    }
    this.checkDisableValue();
  }

  changeAcceptAppraisal() {
    if (this.verificationApprovalEnum === AppraisalDialogApprovalStatus.Godkjent) {
      this.verificationApprovalEnum = AppraisalDialogApprovalStatus.Ubehandlet;
    } else {
      this.verificationApprovalEnum = AppraisalDialogApprovalStatus.Godkjent;
      this.verificationComment = "";
      this.appraisalDeniedReason = null;
    }
    this.checkDisableValue();
  }

  getAppraisalDeniedDescription(reasonKey: number) {
    if (this.appraisalDeniedReasons()) {
      let reason = this.appraisalDeniedReasons().find(x => x.appraisalDeniedReason === reasonKey)
      return reason.appraisalDeniedReasonDescription;
    }
    return null;
  }

  private resetState() {
    this.verificationComment = "";
    this.signature = this.defaultSignature;
    this.validInput = false;
    this.verificationApprovalEnum = AppraisalDialogApprovalStatus.Ubehandlet;
  }

  private checkDisableValue() {
    const isProcessing = this.verificationApprovalEnum != this.appraisalDialogApprovalStatus.Ubehandlet;
    const approveAndSignature = this.verificationApprovalEnum == this.appraisalDialogApprovalStatus.Godkjent && !!this.signature;
    const rejectAndSignatureAndCommentOrReason = this.verificationApprovalEnum == this.appraisalDialogApprovalStatus.Avvist && 
    (this.appraisalDeniedReason || this.verificationComment?.length > 0) && this.signature?.length > 0;
    
    const commercialAppraisalBankReason = this.verificationApprovalEnum == this.appraisalDialogApprovalStatus.Avvist && !!this.signature;
    this.validInput = isProcessing && (approveAndSignature || (this.appraisalValues().valueHistoryType === this.valueHistoryType.CommercialAppraisalBank ? commercialAppraisalBankReason : rejectAndSignatureAndCommentOrReason));
  }

  private postAppraisalApproval(approvalModel: ReportApprovalModel) {
    this.appraisalService.postAppraisalReportApprovalStatus(approvalModel).subscribe(() => this.confirm.emit());
  }
  private postExternalAppraisalApproval(approvalModel: ReportApprovalModel) {
    this.appraisalService.postAppraisalExternalReportApprovalStatus(approvalModel).subscribe(() => this.confirm.emit());
  }


  private getAppraisalReportApprovalHistory(reportId: number) {
    this.appraisalService.getAppraisalReportApprovalHistory(reportId).subscribe(
      result => this._getApprovalHistory(result.data),
    );
  }
  private getExternalAppraisalReportApprovalHistory(reportId: number) {
    this.appraisalService.getExternalAppraisalReportApprovalHistory(reportId).subscribe(
      result => this._getApprovalHistory(result.data),
    );
  }

  private _getApprovalHistory = (res: ReportApprovalStatus[]) => {
    this.appraisalHistory.set(res);
  };
}

export class AppraisalValues {
  constructor(public refId: string, public reportId: number, public valueHistoryType: ValueHistoryType) { }
}